import { connect, DataItem } from 'scrivito'

import { getCardConfig } from './CardView/cardConfig'
import { OverviewCardList } from './CardView/OverviewCardList'
import { useIsCardView } from './CardView/useIsCardView'
import { DataItemDrawers } from './DataItemDrawers'
import { EntityTableRow } from './EntityTableRow'
import { RowSelectPopupContent } from './RowSelectPopupContent'
import { OpenEntityCanvasAction, TableColumn } from './types'
import { useTableSorter } from './useTableSorter'
import { getSelectableIds } from './utils/getSelectableIds'
import { Popup } from '@/Atomic/Molecules/Popup'
import { Table } from '@/Components/Table'
import { getListConfig } from '@/config/listConfig'
import { useMultiSelect } from '@/hooks/useMultiSelect'
import { useDataItemDrawer } from '@/utils/CRUD/hooks/useDataItemDrawer'

interface EntityTableProps {
  tableColumns: TableColumn[]
  dataItems: DataItem[]
  schema: DataItem
  dataClassName?: string
  defaultOrder?: [string, 'desc' | 'asc']
  defaultImage?: string
  imageAlt?: string
  onNavigateTo?: (dataItem: DataItem) => void
  onOpenInNewTab?: (dataItem: DataItem) => void
  omitSidebar?: boolean
  onOpenEntityCanvasCallbackFn?: OpenEntityCanvasAction
}

export const EntityTable: React.FC<EntityTableProps> = connect(
  ({
    tableColumns,
    dataItems,
    schema,
    dataClassName,
    defaultOrder,
    defaultImage,
    imageAlt,
    onNavigateTo,
    onOpenInNewTab,
    omitSidebar,
    onOpenEntityCanvasCallbackFn,
  }) => {
    // Default sort comes from listConfig (single source, shared with the fetch
    // transform in EntityListContext), an explicit prop overriding it. Resolving
    // here keeps each list widget from repeating the lookup; a class with no
    // config default and no prop (e.g. heterogeneous search results) stays unsorted.
    const resolvedDefaultOrder =
      defaultOrder ??
      (dataClassName ? getListConfig(dataClassName).defaultOrder : undefined)
    const { sorter, sortColumn } = useTableSorter(resolvedDefaultOrder)
    const showCardView = useIsCardView(dataClassName)
    const cardConfig = getCardConfig(dataClassName)
    const {
      select,
      isSelected,
      setSelectedIds,
      getSelectedIds,
      isAllSelected,
      isSomeSelected,
    } = useMultiSelect()
    const {
      drawerItem,
      setDrawerItem,
      isDrawerOpen,
      openDrawer,
      closeDrawer,
      openDrawerId,
      startInEditMode,
    } = useDataItemDrawer()

    const onOpenDataItemDrawerClick = (
      e: React.MouseEvent,
      dataItem: DataItem,
      startInEdit: boolean = false,
    ) => {
      e.stopPropagation()
      setDrawerItem(dataItem)
      openDrawer(startInEdit)
    }

    const selectableIds = getSelectableIds(dataItems, dataClassName)

    const isRowSelectDisabled = (id: string) => {
      return !selectableIds.includes(id)
    }

    const isAllRowsSelectDisabled = () => {
      return selectableIds.length <= 0
    }

    const isAllRowsSelected = () => {
      if (isAllRowsSelectDisabled()) {
        return false
      }
      if (isAllSelected(selectableIds)) {
        return true
      }
      if (isSomeSelected(selectableIds)) {
        return undefined
      }
      return false
    }

    const selectAllRows = (select: boolean = true) => {
      if (select) {
        setSelectedIds(selectableIds)
      } else {
        setSelectedIds([])
      }
    }

    const isSomeRowsSelected = () => {
      return isSomeSelected(selectableIds)
    }

    const currentDrawerIndex = dataItems.findIndex(
      (dataItem) => dataItem.id() === drawerItem?.id(),
    )
    const nextItemId = dataItems[currentDrawerIndex + 1]?.id()
    const previousItemId = dataItems[currentDrawerIndex - 1]?.id()

    if (dataItems.length === 0) {
      return null
    }

    return (
      <>
        {showCardView && cardConfig ? (
          <OverviewCardList
            dataItems={dataItems}
            schema={schema}
            config={cardConfig}
            onOpenEntityCanvasCallbackFn={onOpenEntityCanvasCallbackFn}
            onOpenDataItemDrawerClick={onOpenDataItemDrawerClick}
            selectRow={select}
            isRowSelected={isSelected}
            isRowSelectDisabled={isRowSelectDisabled}
            selectAllRows={selectAllRows}
            isAllRowsSelected={isAllRowsSelected}
            isAllRowsSelectDisabled={isAllRowsSelectDisabled}
            hasPopup={isSomeRowsSelected()}
          />
        ) : (
          <Table
            columns={tableColumns}
            sorter={sorter}
            sortColumn={sortColumn}
            selectAllRows={selectAllRows}
            isAllRowsSelected={isAllRowsSelected}
            isAllRowsSelectDisabled={isAllRowsSelectDisabled}
          >
            {dataItems.map((dataItem) => (
              <EntityTableRow
                key={dataItem.id()}
                dataItem={dataItem}
                schema={schema}
                tableColumns={tableColumns}
                defaultImage={defaultImage}
                imageAlt={imageAlt}
                onNavigateTo={onNavigateTo}
                onOpenInNewTab={onOpenInNewTab}
                omitSidebar={omitSidebar}
                onOpenEntityCanvasCallbackFn={onOpenEntityCanvasCallbackFn}
                onOpenDataItemDrawerClick={onOpenDataItemDrawerClick}
                selectRow={select}
                isRowSelected={isSelected}
                isRowSelectDisabled={isRowSelectDisabled}
              />
            ))}
          </Table>
        )}
        <Popup isOpen={isSomeRowsSelected()}>
          <RowSelectPopupContent
            dataClassName={dataClassName}
            selectedIds={getSelectedIds()}
          />
        </Popup>
        <DataItemDrawers
          omitSidebar={omitSidebar}
          drawerItem={drawerItem}
          isOpen={isDrawerOpen}
          close={closeDrawer}
          startInEditMode={startInEditMode}
          openDrawerId={openDrawerId}
          nextId={nextItemId}
          previousId={previousItemId}
        />
      </>
    )
  },
)
